import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const RedLinkStyle = css`
  a {
    color: var(--secondaryColor);
    border-bottom: 1px solid rgba(224, 61, 92, 0.7);
    ${sharedTransition('border-color')}

    &:hover {
      border-color: var(--secondaryColor);
    }
  }
`;

export const Section = styled.section`
  --primaryColor: #360f16;
  --secondaryColor: #e03d5c;
  --primaryBgColor: #fff5f5;

  ${fluidProp(['padding-bottom', 'padding-top'], '100px', '140px')}
  text-align: center;
  font-family: var(--typography-fontBrand);
  color: var(--primaryColor);
`;

export const Title = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  font-family: var(--typography-fontBossa);

  > * {
    font-weight: 500;
  }
`;

export const DescriptionWrapper = styled.div`
  max-width: 618px;
  margin: 0 auto;
  ${RedLinkStyle};
`;

export const Note = styled(DescriptionWrapper)`
  max-width: 748px;
`;

export const BoldNote = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  font-family: var(--typography-fontBossa);
  padding: 20px 0 12px;
`;

export const Card = styled.div`
  max-height: 450px;
  width: 294px;
  background: #fff5f5;
  border-radius: 0px 8px;
  padding: 33px 30px;
  box-sizing: border-box;
  display: flex;
  justify-content: end;
  flex-direction: column;
  ${RedLinkStyle};

  ${BoldNote} {
    > * {
      margin: 0;
    }
  }
`;

const CardWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 32px;
  margin-bottom: 32px;
`;

const ImageWrapper = styled.div`
  max-width: 250px;
`;

export const List = styled(DescriptionWrapper)`
  max-width: unset;
  text-align: left;
  ${RedLinkStyle};

  ul,
  ol {
    margin-top: 46px;
    list-style-type: none;
    flex-wrap: wrap;
    display: flex;
    justify-content: space-between;

    ${tabletBreakpoint(css`
      flex-direction: column;
      align-items: flex-start;
    `)}
  }

  li {
    margin-top: 32px;
    position: relative;

    ${fromTabletBreakpoint(css`
      max-width: 45%;
    `)}

    &::after {
      content: '';
      background-color: var(--secondaryColor);
      -webkit-mask-image: url(${staticAssetUrl('/images/workforce-report/square-check.svg')});
      mask-image: url(${staticAssetUrl('/images/workforce-report/square-check.svg')});
      top: 4px;
      left: -50px;
      width: 24px;
      height: 24px;
      position: absolute;
      opacity: 1;
    }
  }
`;

export function AboutStudySlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { title, description, icon, note, boldNote, list } = primary;

  return (
    <Section>
      <ContentContainer>
        {icon && (
          <Image
            src={icon?.url}
            alt={icon?.alt || ''}
            width={icon?.dimensions.width}
            height={icon?.dimensions.height}
            quality="100"
          />
        )}
        {fieldHasContent(title) && (
          <Title>
            <PrismicRichText field={title} />
          </Title>
        )}
        {fieldHasContent(description) && (
          <DescriptionWrapper>
            <PrismicRichText field={description} />
          </DescriptionWrapper>
        )}
        {fieldHasContent(list) && (
          <List>
            <PrismicRichText field={list} />
          </List>
        )}
        {fieldHasContent(boldNote) && (
          <BoldNote>
            <PrismicRichText field={boldNote} />
          </BoldNote>
        )}
        <CardWrapper>
          {fields.map(({ illustration, title: cardTitle, description: cardDescription }, index) => (
            <Card key={index}>
              {illustration && (
                <ImageWrapper>
                  <Image
                    src={illustration?.url}
                    alt={illustration?.alt || ''}
                    width={illustration?.dimensions.width}
                    height={illustration?.dimensions.height}
                    quality="100"
                  />
                </ImageWrapper>
              )}
              {fieldHasContent(cardTitle) && (
                <BoldNote>
                  <PrismicRichText field={cardTitle} />
                </BoldNote>
              )}
              {fieldHasContent(cardDescription) && (
                <Note>
                  <PrismicRichText field={cardDescription} />
                </Note>
              )}
            </Card>
          ))}
        </CardWrapper>
        {fieldHasContent(note) && (
          <Note>
            <PrismicRichText field={note} />
          </Note>
        )}
      </ContentContainer>
    </Section>
  );
}
